import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberAsMemberNew,
  useMemberInvoiceAsMember,
  useMutationAppointmentLateCancelAsMember,
  useMutationPayInvoiceAsMember,
  useQueryAppointmentIdAsMember,
  useQueryRulesAsPublic,
} from "@gymflow/api";
import { cn, formatCurrency, rulesHelper } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { DateTime } from "luxon";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { Button } from "../../atoms";
import { PaymentMethodPicker } from "../../molecules/PaymentMethodPicker";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";

export const HostedPagesProfileBookingAppointmentsLateCancelCheckout = () => {
  const { isEmbed, createSiteOrEmbedLink } = usePortalRoutes();

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const [currentInvoiceId, setCurrentInvoiceId] = useState<string>();
  const [currentPaymentMethodId, setCurrentPaymentMethodId] =
    useState<string>();

  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { club } = useClubSettings();
  const [parent] = useAutoAnimate();
  const { api } = useGymflowModels();
  const { t } = useTranslation();
  const history = useHistory();
  const showScaModal = useScaModal({
    asMember: true,
  });
  const { toast } = useContext(ToastContext);

  const { data: invoice } = useMemberInvoiceAsMember({
    api,
    invoiceNumber: currentInvoiceId,
  });

  const { mutateAsync: payFailed } = useMutationPayInvoiceAsMember({
    api,
  });

  const { data: userMember } = useMemberAsMemberNew({
    api,
  });

  const { data: dataAppointment } = useQueryAppointmentIdAsMember({
    api,
    id: Number(appointmentId),
  });

  const { data: appointmentRules } = useQueryRulesAsPublic({
    api,
    params: dataAppointment
      ? { appointableId: dataAppointment.appointable.id }
      : undefined,
  });

  const { mutateAsync: mutateAsyncAppointmentLateCancel } =
    useMutationAppointmentLateCancelAsMember({ api });

  const { bookingCancellationRule } =
    rulesHelper.mapRuleGroupToAppointmentRules(appointmentRules);

  useEffect(() => {
    if (invoice?.status === "PAID" || invoice?.status === "PROCESSING") {
      toast({
        message: t("pages.hostedPagesCheckout.invoicePaid"),
        description: t("pages.hostedPagesCheckout.invoicePaidDescription"),
        intent: "success",
      });
      setTimeout(() => {
        history.goBack();
      }, 1500);
    }
  }, [history, invoice?.status, t, toast]);

  const onPay = async () => {
    const result = await mutateAsyncAppointmentLateCancel({
      paymentMethodId: currentPaymentMethodId,
      appointmentId: Number(appointmentId),
    });
    setCurrentInvoiceId(result.invoiceNumber);
    if (result.invoiceNumber) {
      showScaModal({
        invoiceNumber: result.invoiceNumber,
      });
    }
  };

  return (
    <div
      className={cn(
        "track-height flex w-full flex-col items-center overflow-y-auto bg-gray-50 dark:bg-gray-800",
        {
          "h-full": !isEmbed,
          "h-fit": isEmbed,
        },
      )}
    >
      <HostedPagesBackHeader
        label={t(
          "page.hostedPagesProfileBookingAppointmentsLateCancelCheckout.header",
        )}
      />
      <div
        ref={parent}
        className="flex h-fit w-full max-w-lg flex-col gap-4 px-4 py-6"
      >
        {invoice && (
          <div className="flex w-full flex-col items-center gap-y-3">
            <div className="dark:text-gray-25 text-3xl font-bold text-gray-950">
              {formatCurrency(invoice.totalAmount, club.defaultCurrency)}
            </div>
            <div className="flex flex-col items-center gap-1">
              {(invoice.status === "PAID" ||
                invoice.status === "PROCESSING") && (
                <>
                  <div className="text-success-600 dark:text-success-400 text-center font-bold">
                    {t(
                      "page.hostedPagesProfileBookingAppointmentsLateCancelCheckout.paymentSuccessful",
                    )}
                  </div>
                  <div className="font-medium text-gray-600 dark:text-gray-400">
                    {DateTime.fromISO(
                      invoice.issueDate ?? new Date().toISOString(),
                    ).toLocaleString(DateTime.DATE_MED)}
                  </div>
                </>
              )}
              {invoice.status === "PAST_DUE" && (
                <div className="flex flex-col items-center gap-3">
                  <div className="text-error-600 dark:text-error-400 text-center font-bold">
                    {t(
                      "page.hostedPagesProfileBookingAppointmentsLateCancelCheckout.paymentFailed",
                    )}
                  </div>
                  <div className="font-medium text-gray-600 dark:text-gray-400">
                    <Button
                      onClick={async () => {
                        const newInvoice = await payFailed({
                          invoiceNumber: invoice.number,
                          paymentMethodId: currentPaymentMethodId,
                        });
                        showScaModal({
                          invoiceNumber: newInvoice.number,
                        });
                      }}
                    >
                      {t(
                        "page.hostedPagesProfileBookingAppointmentsLateCancelCheckout.retryPayment",
                      )}
                    </Button>
                  </div>
                </div>
              )}
              {invoice.status === "AWAITING_AUTHORIZATION" && (
                <div className="text-warning-600 text-center font-bold">
                  {t(
                    "page.hostedPagesProfileBookingAppointmentsLateCancelCheckout.paymentAwaitingAuthorization",
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {userMember && (
          <div className="max-w-full">
            <PaymentMethodPicker
              userMemberId={userMember.id}
              currentPaymentMethodId={currentPaymentMethodId}
              onChange={setCurrentPaymentMethodId}
              disabled={!!invoice}
              allowBankPayment
              asMember
            />
          </div>
        )}
        <div className="flex w-full border-b-2 border-dashed border-gray-400 dark:border-gray-600" />
        <div className="flex w-full flex-col gap-2">
          {bookingCancellationRule && (
            <div className="flex w-full flex-row justify-between">
              <div className="font-medium text-gray-500 dark:text-gray-400">
                {t(
                  "page.hostedPagesProfileBookingAppointmentsLateCancelCheckout.lateCancellationFee",
                )}
              </div>
              <div className="font-medium text-gray-500 dark:text-gray-400">
                {formatCurrency(
                  bookingCancellationRule.cancellationFee,
                  club.defaultCurrency,
                )}
              </div>
            </div>
          )}
          {bookingCancellationRule && (
            <div className="dark:text-gray-25 flex w-full flex-row items-center justify-between text-gray-950">
              <div className="text-xl font-bold">
                {t(
                  "page.hostedPagesProfileBookingAppointmentsLateCancelCheckout.amountDue",
                )}
              </div>
              <div className="text-xl font-bold">
                {formatCurrency(
                  bookingCancellationRule.cancellationFee,
                  club.defaultCurrency,
                )}
              </div>
            </div>
          )}
        </div>
        {!invoice && (
          <div className="flex w-full flex-col gap-1">
            <Button
              intent="secondary"
              className="w-full"
              disabled={!bookingCancellationRule || !currentPaymentMethodId}
              onClick={() => onPay()}
            >
              {t("portal.common.payAmount", {
                amount: formatCurrency(
                  bookingCancellationRule?.cancellationFee ?? 0,
                  club.defaultCurrency,
                ),
              })}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
